import React from "react";
import { Box, Grid, makeStyles, createStyles } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import withAuthority from "../../../../../../components/Auth/withAuthority";
import Authorities from "../../../../../../auth/authorities";
import { CustomTheme } from "../../../../../../types/customTheme";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    container: {
      padding: "24px",
      backgroundColor: theme.palette.background.default,
      minHeight: "100vh",
    },
    toolbarSection: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "12px",
      marginBottom: "16px",
    },
    leftToolbar: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
    },
    rightToolbar: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    searchBar: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "0 12px",
      width: "200px",
      height: "40px",
      borderRadius: "8px",
      border: "1px solid #374151",
      backgroundColor: theme.palette.background.entity_background,
    },
    buttonSkeleton: {
      borderRadius: "8px",
    },
    toggleButton: {
      width: "40px",
      height: "40px",
      borderRadius: "8px",
    },
    breadcrumbSection: {
      padding: "12px 24px",
      marginBottom: "16px",
      backgroundColor: theme.palette.background.default,
    },
    categorySection: {
      marginBottom: "32px",
    },
    categoryHeader: {
      backgroundColor: theme.palette.background.default,
      padding: "20px 24px 10px 24px",
      marginBottom: "16px",
    },
    categoryInfo: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "16px",
    },
    categoryIcon: {
      width: "40px",
      height: "40px",
      borderRadius: "10px",
    },
    categoryStats: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "16px",
      marginTop: "20px",
      marginBottom: "24px",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(2, 1fr)",
      },
    },
    statCard: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: "10px",
      padding: "12px",
      display: "flex",
      alignItems: "center",
      height: "80px",
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    itemsGrid: {
      padding: "0 24px 24px 24px",
    },
    itemCard: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: "10px",
      border: `1px solid ${theme.palette.background.entity_border}`,
      padding: "16px",
      height: "180px",
      display: "flex",
      gap: "12px",
    },
    itemImage: {
      width: "114px",
      height: "114px",
      borderRadius: "10px",
      flexShrink: 0,
    },
    itemContent: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    itemHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "8px",
    },
    itemActions: {
      display: "flex",
      gap: "8px",
      justifyContent: "flex-end",
    },
    addButton: {
      width: "100%",
      marginTop: "14px",
      height: "100px",
      borderRadius: "10px",
      border: `1px dashed ${theme.palette.custom.yellow.main}`,
      backgroundColor: theme.palette.background.entity_background,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
    },
  })
);

const DirectorySkeletonLoading = () => {
  const classes = useStyles();

  return (
    <Box className={classes.container}>
      {/* Toolbar Section */}
      <Box className={classes.toolbarSection}>
        <Box className={classes.leftToolbar}>
          {/* Back Button */}
          <Skeleton variant="rect" width={120} height={36} className={classes.buttonSkeleton} />
          
          {/* Search Bar */}
          <Box className={classes.searchBar}>
            <Skeleton variant="circle" width={18} height={18} />
            <Skeleton variant="text" width={120} height={20} />
          </Box>
          
          {/* Availability Button */}
          <Skeleton variant="rect" width={100} height={40} className={classes.buttonSkeleton} />
          
          {/* Show Hidden Toggle */}
          <Box display="flex" alignItems="center" style={{gap: "1px"}}>
            <Skeleton variant="rect" width={34} height={18} style={{ borderRadius: 10 }} />
            <Skeleton variant="text" width={80} height={20} />
          </Box>
        </Box>
        
        <Box className={classes.rightToolbar}>
          {/* View Toggle Buttons */}
          <Skeleton variant="rect" width={40} height={40} className={classes.toggleButton} />
          <Skeleton variant="rect" width={40} height={40} className={classes.toggleButton} />
        </Box>
      </Box>

      {/* Breadcrumb Section */}
      <Box className={classes.breadcrumbSection}>
        <Box display="flex" alignItems="center" style={{gap: "1px"}}>
          <Skeleton variant="text" width={80} height={24} />
          <Skeleton variant="text" width={16} height={24} />
          <Skeleton variant="text" width={120} height={24} />
          <Skeleton variant="text" width={16} height={24} />
          <Skeleton variant="text" width={100} height={24} />
        </Box>
      </Box>

      {/* Category Sections */}
      {[1, 2].map((category) => (
        <Box key={category} className={classes.categorySection}>
          {/* Category Header */}
          <Box className={classes.categoryHeader}>
            <Box className={classes.categoryInfo}>
              <Skeleton variant="rect" className={classes.categoryIcon} />
              <Box flex={1}>
                <Skeleton variant="text" width={200} height={28} />
                <Skeleton variant="text" width={150} height={16} />
              </Box>
              <Box display="flex" style={{gap: "1px"}}>
                <Skeleton variant="rect" width={32} height={32} style={{ borderRadius: 6 }} />
                <Skeleton variant="rect" width={32} height={32} style={{ borderRadius: 6 }} />
              </Box>
            </Box>

            {/* Stats Cards */}
            <Box className={classes.categoryStats}>
              {[1, 2, 3, 4].map((stat) => (
                <Box key={stat} className={classes.statCard}>
                  <Skeleton variant="circle" width={44} height={44} style={{ marginRight: 16 }} />
                  <Box flex={1}>
                    <Skeleton variant="text" width={60} height={24} />
                    <Skeleton variant="text" width={80} height={16} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Items Grid */}
          <Grid container spacing={2} className={classes.itemsGrid}>
            {[1, 2, 3].map((item) => (
              <Grid item xs={12} md={6} lg={4} key={item}>
                <Box className={classes.itemCard}>
                  <Skeleton variant="rect" className={classes.itemImage} />
                  <Box className={classes.itemContent}>
                    <Box>
                      <Box className={classes.itemHeader}>
                        <Skeleton variant="text" width={100} height={24} />
                        <Skeleton variant="rect" width={60} height={20} style={{ borderRadius: 10 }} />
                      </Box>
                      <Skeleton variant="text" width="100%" height={16} />
                      <Skeleton variant="text" width="80%" height={16} />
                      <Skeleton variant="text" width="60%" height={16} />
                    </Box>
                    <Box className={classes.itemActions}>
                      <Skeleton variant="rect" width={28} height={28} style={{ borderRadius: 6 }} />
                      <Skeleton variant="rect" width={28} height={28} style={{ borderRadius: 6 }} />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
            
            {/* Add New Item Button Skeleton */}
            <Grid item xs={12} md={6} lg={4}>
              <Box className={classes.addButton}>
                <Skeleton variant="circle" width={24} height={24} />
                <Skeleton variant="text" width={80} height={20} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default withAuthority(DirectorySkeletonLoading, Authorities.DASHBOARD_READ);